import Form from "@/components/Form";
import Field from "@/components/Field";
import SaveButton from "@/components/SaveButton";
import PageContent from "@/components/PageContent";
import Fieldset from "@/components/Fieldset";

import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutateFunction } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Module, YM_DATE_FORMAT } from "@/constants";
import { isRequired } from "@/lib/form";
import DatePicker from "@/components/DatePicker";
import { Input } from "@/components/ui/input";
import {
  invoiceSpecificationSchema,
  invoiceSpecificationUpdateSchema,
} from "@/schemas/invoiceSpecification";
import { FilePenLine } from "lucide-react";
import { defaultUpdateValues } from "../defaultUpdateValues";
import { ApiError } from "@/services/api";
import { formatDate } from "date-fns";
import { formatDateTime } from "@/lib/utils/formatters";
import { CalendarIcon } from "lucide-react";
import {
  UpdateInvoiceSpecification,
  ReadInvoiceSpecification,
} from "@/types/invoiceSpecification";
import { useEffect } from "react";

interface BasicDataFormProps<TFormData = UpdateInvoiceSpecification> {
  values?: Partial<ReadInvoiceSpecification>;
  disabled?: boolean;
  onSubmit: UseMutateFunction<ReadInvoiceSpecification, ApiError, TFormData>;
  projectName?: string;
  transformData?: (data: UpdateInvoiceSpecification) => TFormData;
  isEditMode?: boolean; // externally supplied flag instead of deriving internally
  footer?: React.ReactNode;
  isLoading?: boolean;
}

export const BasicDataForm = <TFormData = UpdateInvoiceSpecification,>({
  values = defaultUpdateValues,
  onSubmit,
  disabled = false,
  projectName,
  transformData,
  isEditMode = false,
  footer,
  isLoading = false,
}: BasicDataFormProps<TFormData>) => {
  const { t } = useTranslation();
  const { can_update, can_create } = useModulePermissions(
    Module.InvoiceSpecifications,
  );
  const isFormDisabled = disabled || !can_update || !can_create;
  const form = useForm<UpdateInvoiceSpecification>({
    resolver: zodResolver(invoiceSpecificationUpdateSchema),
    defaultValues: isEditMode ? values : {},
    disabled: isFormDisabled,
  });

  useFormUnsavedChangesWarning(form);

  const handleSubmit = (data: UpdateInvoiceSpecification) => {
    const transformedData = transformData
      ? transformData(data)
      : (data as TFormData);
    onSubmit(transformedData);
  };

  // Hack to mark form dirty when all needed fields are filled with default values during create
  useEffect(() => {
    if (isEditMode) {
      return;
    }

    form.reset(values, { keepDefaultValues: true });
  }, [values, form, isEditMode]);

  // Reset form to clear dirty state when values change in edit mode (after successful save)
  useEffect(() => {
    if (!isEditMode || !values) {
      return;
    }

    const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
    // Reset only if form is not dirty to avoid resetting user's changes (after successful save and refetch)
    if (!isDirty) {
      form.reset(values);
    }
  }, [values, form, isEditMode]);

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <PageContent
        icon={<FilePenLine className="w-5 h-5 mr-2" />}
        title={t("projectInvoiceSpecifications.sections.basicData.header")}
        footer={footer ?? <SaveButton isLoading={isLoading} />}
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {isEditMode && (
            <Field
              name="ref_number"
              label={t("projectInvoiceSpecifications.fields.ref_number")}
              required={isRequired(invoiceSpecificationSchema.shape.ref_number)}
              render={({ field }) => (
                <Input className="w-full" {...field} disabled={true} />
              )}
            />
          )}
          <Field
            name="period"
            label={t("projectInvoiceSpecifications.fields.period")}
            required={isRequired(invoiceSpecificationSchema.shape.period)}
            render={({ field }) => (
              <Input
                className="w-full"
                {...field}
                value={
                  field.value ? formatDate(field.value, YM_DATE_FORMAT) : ""
                }
                disabled={true}
              />
            )}
          />
          <Field
            name="project_name"
            label={t("projectInvoiceSpecifications.fields.project_name")}
            required={false}
            render={({ field }) => (
              <Input
                className="w-full"
                {...field}
                value={projectName}
                disabled={true}
              />
            )}
          />
          <Field
            name="invoicing_company"
            label={t("projectInvoiceSpecifications.fields.invoicing_company")}
            required={isRequired(
              invoiceSpecificationSchema.shape.invoicing_company,
            )}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
          <Field
            name="issued_at"
            label={t("projectInvoiceSpecifications.fields.issued_at")}
            required={isRequired(invoiceSpecificationSchema.shape.issued_at)}
            wrapperClassName="flex-1"
            render={({ field }) => (
              <DatePicker
                className="w-full"
                {...field}
                disabled={isFormDisabled}
              />
            )}
          />
          <Field
            name="po_number"
            label={t("projectInvoiceSpecifications.fields.po_number")}
            required={isRequired(invoiceSpecificationSchema.shape.po_number)}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={isFormDisabled} />
            )}
          />
          {isEditMode && (
            <Field
              name="created_at"
              label={t("projectInvoiceSpecifications.fields.created_at")}
              required={isRequired(invoiceSpecificationSchema.shape.created_at)}
              wrapperClassName="flex-1"
              render={({ field }) => (
                <span className="flex flex-row gap-1 items-center text-muted-foreground">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  <Input
                    className="w-full"
                    {...field}
                    value={formatDateTime(field.value)}
                    disabled={true}
                  />
                </span>
              )}
            />
          )}
          <Field
            name="code"
            label={t("projectInvoiceSpecifications.fields.code")}
            required={isRequired(invoiceSpecificationSchema.shape.code)}
            render={({ field }) => (
              <>
                <input type="hidden" {...field} />
                <Input className="w-full" {...field} disabled={true} />
              </>
            )}
          />
          <Field
            name="services_and_goods"
            label={t("projectInvoiceSpecifications.fields.services_and_goods")}
            required={isRequired(
              invoiceSpecificationSchema.shape.services_and_goods,
            )}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
          <Field
            name="services_and_goods_code"
            label={t(
              "projectInvoiceSpecifications.fields.services_and_goods_code",
            )}
            required={isRequired(
              invoiceSpecificationSchema.shape.services_and_goods_code,
            )}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
          <Field
            name="services_and_goods_invoice_description"
            label={t(
              "projectInvoiceSpecifications.fields.services_and_goods_invoice_description",
            )}
            required={isRequired(
              invoiceSpecificationSchema.shape
                .services_and_goods_invoice_description,
            )}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
          <Field
            name="primary_manager"
            label={t("projectInvoiceSpecifications.fields.primary_manager")}
            required={isRequired(
              invoiceSpecificationSchema.shape.primary_manager,
            )}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
        </Fieldset>
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="client"
            label={t("projectInvoiceSpecifications.fields.client")}
            required={isRequired(invoiceSpecificationSchema.shape.client)}
            render={({ field }) => {
              return <Input className="w-full" {...field} disabled={true} />;
            }}
          />
          <Field
            name="client_code"
            label={t("projectInvoiceSpecifications.fields.client_code")}
            required={isRequired(invoiceSpecificationSchema.shape.client_code)}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
          <Field
            name="client_contact_invoice"
            label={t(
              "projectInvoiceSpecifications.fields.client_contact_invoice",
            )}
            required={isRequired(
              invoiceSpecificationSchema.shape.client_contact_invoice,
            )}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={true} />
            )}
          />
        </Fieldset>
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="comment"
            label={t("projectInvoiceSpecifications.fields.comment")}
            required={isRequired(invoiceSpecificationSchema.shape.comment)}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={isFormDisabled} />
            )}
          />
        </Fieldset>
      </PageContent>
    </Form>
  );
};
